import { DistanceCoordinate, CardinalDirection, BoundingBox } from './coordinate.model';
import { computed, inject, Injectable } from '@angular/core';
import { UserProfileFacade } from '../../store/user-profile';
import { toObservable } from '@angular/core/rxjs-interop';

export enum UnitSystem {
  Imperial = 0,
  Metric = 1
}

type Coordinates = [number, number] | [number, number][] | [number, number][][] | [number, number][][][];

@Injectable({ providedIn: 'root' })
export class DistanceService {
  private usersDistanceUnitPref = inject(UserProfileFacade).distanceUnit;
  readonly unitSystem = computed(() => {
    const distanceUnit = this.usersDistanceUnitPref() ?? 'miles';
    return distanceUnit?.toLowerCase() === 'miles' ? UnitSystem.Imperial : UnitSystem.Metric;
  });
  readonly unitSystem$ = toObservable(this.unitSystem);

  /**
   * Gets the distance of a pixel in meters at a given zoom level
   * @param zoom the google map zoom level
   * @param lat  the current latitude (needed since map is a mercator project and pixels are stretched at the poles)
   * @param precision
   */
  public getDistanceFromPixels(zoom: number, lat: number, precision: number): number {
    const earthCircumferenceMeters = 40075000;
    const groundResolution = (Math.cos((lat * Math.PI) / 180) * earthCircumferenceMeters) / (256 * Math.pow(2, zoom));
    return groundResolution;
  }

  /**
   * Given two coordinates will measure the distance between them in meters
   * @param from a coordinate from where to start the calculation
   * @param to a coordinate where to end the calculation
   */
  public measureDistance(from: DistanceCoordinate, to: DistanceCoordinate): number {
    return google.maps.geometry.spherical.computeDistanceBetween(
      new google.maps.LatLng(from.lat, from.lng),
      new google.maps.LatLng(to.lat, to.lng)
    );
  }

  /**
   * find the coordinate on a line which is closest to a point
   * @param p point from which to calculate
   * @param a first point in line
   * @param b second point in line
   */
  private findClosestPointOnLine(
    pXy: { y: number; x: number },
    aXy: { y: number; x: number },
    bXy: { y: number; x: number }
  ): { y: number; x: number } {
    const aXys = [aXy, bXy];

    let minDist;
    let fTo;
    let i;
    let dist;

    for (let n = 1; n < aXys.length; n++) {
      if (aXys[n].x != aXys[n - 1].x) {
        const a = (aXys[n].y - aXys[n - 1].y) / (aXys[n].x - aXys[n - 1].x);
        const b = aXys[n].y - a * aXys[n].x;
        dist = Math.abs(a * pXy.x + b - pXy.y) / Math.sqrt(a * a + 1);
      } else {
        dist = Math.abs(pXy.x - aXys[n].x);
      }

      // length^2 of line segment
      const rl2 = Math.pow(aXys[n].y - aXys[n - 1].y, 2) + Math.pow(aXys[n].x - aXys[n - 1].x, 2);

      // distance^2 of pt to end line segment
      const ln2 = Math.pow(aXys[n].y - pXy.y, 2) + Math.pow(aXys[n].x - pXy.x, 2);

      // distance^2 of pt to begin line segment
      const lnm12 = Math.pow(aXys[n - 1].y - pXy.y, 2) + Math.pow(aXys[n - 1].x - pXy.x, 2);

      // minimum distance^2 of pt to infinite line
      const dist2 = Math.pow(dist, 2);

      // calculated length^2 of line segment
      const calcrl2 = ln2 - dist2 + lnm12 - dist2;

      // redefine minimum distance to line segment (not infinite line) if necessary
      if (calcrl2 > rl2) {
        dist = Math.sqrt(Math.min(ln2, lnm12));
      }

      if (minDist == null || minDist > dist) {
        if (calcrl2 > rl2) {
          if (lnm12 < ln2) {
            fTo = 0; //nearer to previous point
          } else {
            fTo = 1; // nearer to current point
          }
        } else {
          // perpendicular from point intersects line segment
          fTo = Math.sqrt(lnm12 - dist2) / Math.sqrt(rl2);
        }
        minDist = dist;
        i = n;
      }
    }

    const dx = aXys[i - 1].x - aXys[i].x;
    const dy = aXys[i - 1].y - aXys[i].y;

    const c = {
      y: aXys[i - 1].y - dy * fTo,
      x: aXys[i - 1].x - dx * fTo
    };
    return c;
  }

  /**
   * Given 1 coordinate and Bounds, will measure the distance between the closest point
   * between them, in meters
   * @param point a coordinate from where to start the calculation
   * @param bounds a set of bounds for which to find the shorest distance to
   */
  public measureDistanceFromBounds(point: DistanceCoordinate, bounds: [number, number][]): number {
    // upper right = max(y) max(x)
    const boundsPolygon: { y: number; x: number }[] = [];
    boundsPolygon.push({ y: Math.max(bounds[0][0], bounds[1][0]), x: Math.min(bounds[0][1], bounds[1][1]) });
    boundsPolygon.push({ y: Math.max(bounds[0][0], bounds[1][0]), x: Math.max(bounds[0][1], bounds[1][1]) });
    boundsPolygon.push({ y: Math.min(bounds[0][0], bounds[1][0]), x: Math.max(bounds[0][1], bounds[1][1]) });
    boundsPolygon.push({ y: Math.min(bounds[0][0], bounds[1][0]), x: Math.min(bounds[0][1], bounds[1][1]) });
    const p = { y: point.lat, x: point.lng };
    // .log(bounds);

    const calculatedPoints: { y: number; x: number }[] = boundsPolygon.map((val, i, arr) => {
      if (i === 0) {
        return this.findClosestPointOnLine(p, arr[arr.length - 1], arr[i]);
      } else {
        return this.findClosestPointOnLine(p, arr[i - 1], arr[i]);
      }
    });
    // .log(calculatedPoints.map((c) => {
    //   const d = google.maps.geometry.spherical.computeDistanceBetween(
    //     new google.maps.LatLng(c.y, c.x),
    //     new google.maps.LatLng(p.y, p.x));
    //   .log(`c: ${JSON.stringify(c)} p: ${JSON.stringify(c)} d: ${d} `);
    //   return d;
    // }).sort((n, n2) => n - n2));
    const minDist = calculatedPoints
      .map(c => google.maps.geometry.spherical.computeDistanceBetween(new google.maps.LatLng(c.y, c.x), new google.maps.LatLng(p.y, p.x)))
      .sort((n, n2) => n - n2)[0];

    return minDist;
  }
  public boundsArrayFromBounds(bounds: google.maps.LatLngBounds): [number, number][] {
    // const boundsArray: [number, number][] =
    // [[bounds.nwCoordinate.lat, bounds.nwCoordinate.lng], [bounds.seCoordinate.lat, bounds.seCoordinate.lng]];

    const boundsArray: [number, number][] = [
      [bounds.getNorthEast().lat(), bounds.getSouthWest().lng()],
      [bounds.getSouthWest().lat(), bounds.getNorthEast().lng()]
    ];

    return boundsArray;
  }
  public boundsToGeoJsonPolygon(bounds: google.maps.LatLngBounds): [number, number][][] {
    return [
      [
        [bounds.getSouthWest().lng(), bounds.getNorthEast().lat()], // NW
        [bounds.getSouthWest().lng(), bounds.getSouthWest().lat()], // SW
        [bounds.getNorthEast().lng(), bounds.getSouthWest().lat()], // SE
        [bounds.getNorthEast().lng(), bounds.getNorthEast().lat()], // NE
        [bounds.getSouthWest().lng(), bounds.getNorthEast().lat()] // NW
      ]
    ];
  }

  /**
   * Given a GeoJson feature, find the Google maps zoom level at which the shape will cover at least N pixels
   * @param element a GeoJson feature
   * @param pixelsToCover the shape will cover at least N pixels
   */
  public findMinGoogleZoomToCoverAtLeastNPixelsForFeature(element: any, pixelsToCover: number = 20): number {
    if (element.type === 'point' || element.type === 'Point') {
      // Points can never cover too few pixels
      return 0;
    }
    // this.extendBoundsFromCoordinatesTypeAndRadius(bounds, element.coordinates, element.type, element.radius);
    // https://groups.google.com/forum/#!topic/google-maps-js-api-v3/hDRO4oHVSeM
    // How to calculate number of meters per pixel at zoom and latitude
    // 156543.03392 * Math.cos(68 * Math.PI / 180) / Math.pow(2, 6)
    const bounds = this.getBoundsForGeometryFeatures([element]);
    const distance = google.maps.geometry.spherical.computeDistanceBetween(bounds.getNorthEast(), bounds.getSouthWest());
    if (distance < 1) {
      return 17;
    }
    // find min zoom where at least X pixels are covered
    let zoom = 1;
    while (zoom < 18) {
      const metersPerPixelAtLat1 = (156543.03392 * Math.cos((bounds.getNorthEast().lat() * Math.PI) / 180)) / Math.pow(2, zoom);
      const metersPerPixelAtLat2 = (156543.03392 * Math.cos((bounds.getSouthWest().lat() * Math.PI) / 180)) / Math.pow(2, zoom);
      if (distance / metersPerPixelAtLat1 >= pixelsToCover || distance / metersPerPixelAtLat2 >= pixelsToCover) {
        return zoom;
      }
      zoom++;
    }
    return zoom;
  }

  /**
   * Given a GeoJson feature, find the Google maps zoom level at which the shape will cover at most N pixels
   * @param geometryArray an array of GeoJson features
   * @param pixelsToCover the shape will cover at most N pixels
   */
  public findMinGoogleZoomToCoverAtMostNPixelsForFeature(geometryArray: any[], pixelsToCover: number = 20): number {
    // https://groups.google.com/forum/#!topic/google-maps-js-api-v3/hDRO4oHVSeM
    // How to calculate number of meters per pixel at zoom and latitude
    // 156543.03392 * Math.cos(68 * Math.PI / 180) / Math.pow(2, 6)
    const bounds = this.getBoundsForGeometryFeatures(geometryArray);
    const distanceY = google.maps.geometry.spherical.computeDistanceBetween(
      new google.maps.LatLng(bounds.getNorthEast().lat(), bounds.getSouthWest().lng()),
      new google.maps.LatLng(bounds.getSouthWest().lat(), bounds.getSouthWest().lng())
    );
    const distanceX = google.maps.geometry.spherical.computeDistanceBetween(
      new google.maps.LatLng(bounds.getSouthWest().lat(), bounds.getNorthEast().lng()),
      new google.maps.LatLng(bounds.getSouthWest().lat(), bounds.getSouthWest().lng())
    );
    if (distanceX < 1 && distanceY < 1) {
      return 25;
    }
    // find min zoom where at most X pixels are covered
    let zoom = 25;
    while (zoom > 4) {
      const metersPerPixelAtLat1 = (156543.03392 * Math.cos((bounds.getNorthEast().lat() * Math.PI) / 180)) / Math.pow(2, zoom);
      const metersPerPixelAtLat2 = (156543.03392 * Math.cos((bounds.getSouthWest().lat() * Math.PI) / 180)) / Math.pow(2, zoom);
      if (
        distanceY / metersPerPixelAtLat1 <= pixelsToCover &&
        distanceY / metersPerPixelAtLat2 <= pixelsToCover &&
        distanceX / metersPerPixelAtLat1 <= pixelsToCover &&
        distanceX / metersPerPixelAtLat2 <= pixelsToCover
      ) {
        return zoom;
      }
      zoom--;
    }
    return zoom;
  }

  public getBoundsForGeometryFeatures(geometryFeatures: any[]): google.maps.LatLngBounds {
    const bounds = new google.maps.LatLngBounds();

    geometryFeatures.forEach(element => {
      const geo = element.type === 'Feature' ? element.geometry : element;
      if (!!geo) {
        this.extendBoundsFromCoordinatesTypeAndRadius(bounds, geo.coordinates, geo.type, geo.radius);
      }
    });
    return bounds;
  }

  public getBoundsFromCoordinateAndRadiusBuffer(
    point: { lat: number; lng: number },
    radiusM: number
  ): { ne: { lat: number; lng: number }; sw: { lat: number; lng: number } } {
    const bounds = new google.maps.LatLngBounds();

    this.extendBoundsFromCoordinatesTypeAndRadius(bounds, [point.lng, point.lat], 'circle', radiusM);
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    return { ne: { lat: ne.lat(), lng: ne.lng() }, sw: { lat: sw.lat(), lng: sw.lng() } };
  }

  public extendBoundsFromCoordinatesTypeAndRadius(
    bounds: google.maps.LatLngBounds,
    coordinates: any,
    type: string,
    radius?: number
  ): google.maps.LatLngBounds {
    const flattenedArray = [];
    const arrayStackToFlatten = [];
    arrayStackToFlatten.push(coordinates);
    // find bounds of deep coordinates arrays
    while (arrayStackToFlatten.length > 0) {
      // Pop array
      const arr = arrayStackToFlatten.pop();
      // if array is empty
      if (!arr || arr[0] === undefined) {
        continue;
      }
      // If array is of points
      if (arr[0].length === 2 && !isNaN(arr[0][0])) {
        flattenedArray.push(arr);
        continue;
      }
      // if array is a point
      if (!isNaN(arr[0])) {
        const a = arr as [number, number];
        flattenedArray.push([a]);
        continue;
      }
      // If array is of arrays
      arr.forEach(a => arrayStackToFlatten.push(a));
    }

    flattenedArray.forEach(array =>
      array.forEach(point => {
        if (typeof point[0] === 'number' && typeof point[1] === 'number') {
          bounds.extend(new google.maps.LatLng(point[1], point[0]));
        }
      })
    );
    if (type === 'circle') {
      const p1 = new google.maps.LatLng(flattenedArray[0][0][1], flattenedArray[0][0][0]);
      let p2 = google.maps.geometry.spherical.computeOffset(p1, radius, 0);
      bounds.extend(new google.maps.LatLng(p2.lat(), p2.lng()));
      p2 = google.maps.geometry.spherical.computeOffset(p1, radius, 90);
      bounds.extend(new google.maps.LatLng(p2.lat(), p2.lng()));
      p2 = google.maps.geometry.spherical.computeOffset(p1, radius, 180);
      bounds.extend(new google.maps.LatLng(p2.lat(), p2.lng()));
      p2 = google.maps.geometry.spherical.computeOffset(p1, radius, 270);
      bounds.extend(new google.maps.LatLng(p2.lat(), p2.lng()));
    }
    return bounds;
  }

  public geometryIntersectsBounds(geometryFeatures: any[], bounds: google.maps.LatLngBounds): boolean {
    return !!geometryFeatures.find(geometry => {
      const geoBounds = this.getBoundsForGeometryFeatures([geometry]);

      return (
        geoBounds.intersects(bounds) || this.doesBoundsContainBounds(geoBounds, bounds) || this.doesBoundsContainBounds(bounds, geoBounds)
      );
    });
  }

  public doesBoundsContainBounds(boundsA: google.maps.LatLngBounds, boundsB: google.maps.LatLngBounds) {
    if (!boundsB || !boundsB?.getNorthEast()) {
      return;
    }

    if (!boundsA || !boundsA?.getSouthWest()) {
      return;
    }

    return !(!boundsB.contains(boundsA.getNorthEast()) || !boundsB.contains(boundsA.getSouthWest()));
  }
  /**
   * Measures the distance between a coordinate and a geometry
   * @param from the coordinate from where to start
   * @param geometry a geometry (we will use the center of it) following geojson standard
   */
  public measureMinimumDistanceFromGeometriesToGeometries(fromGeometries: any[], toGeometries: any[]): number {
    let minimumDistance = Number.MAX_SAFE_INTEGER;
    fromGeometries.forEach(fromGeoFeature => {
      const fromGeometry = fromGeoFeature.type === 'Feature' ? fromGeoFeature.geometry : fromGeoFeature;
      let fromGeometryRadius = 0;
      const fromGeometryType = fromGeometry.type.toLowerCase();
      if (fromGeometryType === 'circle') {
        fromGeometryRadius = parseInt(fromGeometry.radius, 10);
      }

      toGeometries.forEach(toGeoFeature => {
        const toGeometry = toGeoFeature.type === 'Feature' ? toGeoFeature.geometry : toGeoFeature;
        let toGeometryRadius = 0;
        const toGeometryType = toGeometry.type.toLowerCase();
        if (toGeometryType === 'circle') {
          toGeometryRadius = parseInt(toGeometry.radius, 10);
        }
        // fresh copy of all coordinates to iterate through
        const fromGeometryPointsAr = this.normalizeToEdges(fromGeometry.coordinates || []);
        const toGeometryPointsAr = this.normalizeToEdges(toGeometry.coordinates || []);
        const fromLines = [] as [number, number][][];
        const toLines = [] as [number, number][][];
        fromGeometryPointsAr.forEach(poly => poly.forEach(line => fromLines.push(line)));
        toGeometryPointsAr.forEach(poly => poly.forEach(line => toLines.push(line)));

        fromLines.forEach(fromLine => {
          const centerOfFromLine = this.getCenterFromPolygon(fromLine);
          toLines.forEach(toLine => {
            const fQ = fromLine.slice();
            while (fQ.length > 0) {
              const fLineSegStart = fQ.shift();
              const fLineSegEnd = fQ.length === 0 ? null : fQ.shift();
              const tQ = toLine.slice();
              while (tQ.length > 0) {
                const tLineSegStart = tQ.shift();
                const tLineSegEnd = tQ.length === 0 ? null : tQ.shift();
                // When a line, find the point on the line closest to the center of the from line
                const pointB = !tLineSegEnd
                  ? tLineSegStart
                  : Object.values(
                      this.findClosestPointOnLine(
                        { y: centerOfFromLine.lat, x: centerOfFromLine.lng },
                        { y: tLineSegStart[1], x: tLineSegStart[0] },
                        { y: tLineSegEnd[1], x: tLineSegEnd[0] }
                      )
                    ).reverse();

                // When a line, find the point on the line closest to PointB
                const pointA = !fLineSegEnd
                  ? fLineSegStart
                  : Object.values(
                      this.findClosestPointOnLine(
                        { y: pointB[1], x: pointB[0] },
                        { y: fLineSegStart[1], x: fLineSegStart[0] },
                        { y: fLineSegEnd[1], x: fLineSegEnd[0] }
                      )
                    ).reverse();
                const distance =
                  google.maps.geometry.spherical.computeDistanceBetween(
                    new google.maps.LatLng(pointA[1], pointA[0]),
                    new google.maps.LatLng(pointB[1], pointB[0])
                  ) -
                  (fromGeometryRadius + toGeometryRadius);

                const newDistance = Math.max(0, distance);
                if (minimumDistance > newDistance) {
                  minimumDistance = newDistance;
                }
              }
            }
          });
        });
      });
    });

    return parseFloat(minimumDistance.toFixed(2));
  }

  /**
   * Measures the distance between a coordinate and a geometry
   * @param from the coordinate from where to start
   * @param geometry a geometry (we will use the center of it) following geojson standard
   */
  public measureDistanceFromPointToGeometry(from: DistanceCoordinate, geometry: any): number {
    let center: DistanceCoordinate;
    switch (geometry.type.toLowerCase()) {
      case 'marker':
      case 'point':
      case 'circle':
        center = new DistanceCoordinate(geometry.coordinates[1], geometry.coordinates[0]);
        break;
      case 'multipolygon':
      case 'polygon':
        center = this.getCenterFromPolygon(geometry.coordinates);
        break;
      case 'linestring':
      case 'multilinestring':
        center = this.getCenterFromPolyline(geometry.coordinates);
    }

    const distance = google.maps.geometry.spherical.computeDistanceBetween(
      new google.maps.LatLng(from.lat, from.lng),
      new google.maps.LatLng(center.lat, center.lng)
    );

    return parseFloat(distance.toFixed(2));
  }

  /**
   * Given a bounding box, will return the center of it
   * @param southWest south-west coordinates
   * @param northEast north-east coordinates
   */
  public getCenterFromBoundingBox(southWest: DistanceCoordinate, northEast: DistanceCoordinate): DistanceCoordinate {
    const bounds = new google.maps.LatLngBounds(
      new google.maps.LatLng(southWest.lat, southWest.lng),
      new google.maps.LatLng(northEast.lat, northEast.lng)
    );
    const center = bounds.getCenter();

    return new DistanceCoordinate(center.lat(), center.lng());
  }

  /**
   * given a distance measured in meters, return a readable version of it
   * @param distance the distance to convert
   */
  public parseToFriendlyDistance(distance: number, useCommas: boolean = false): string {
    if (this.unitSystem() === UnitSystem.Imperial) {
      // Convert to decimal miles
      const mi = distance * 0.00062137;
      return (useCommas ? this.numberWithCommas(mi.toFixed(2)) : mi.toFixed(2)) + ' mi';
    }
    return (useCommas ? this.numberWithCommas((distance / 1000).toFixed(2)) : (distance / 1000).toFixed(2)) + ' km';
  }

  /**
   * given a distance measured in meters, return the number in the selected whole units
   * @param distance the distance to convert
   */
  public parseToDistanceToWholeUnits(distance: number): number {
    if (this.unitSystem() === UnitSystem.Imperial) {
      // Convert to decimal miles
      return distance * 0.00062137;
    }
    // Convert to decimal km
    return distance * 0.001;
  }

  private numberWithCommas(x: string) {
    const parts = x.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  }

  /**
   * given a distance measured in meters, return a readable version of it with the specified number of decimal places
   * @param distance the distance to convert
   * @param decimals Array of decimals to use. Last will be used when distance is greater than all breakpoints.
   * @param distanceBreakpoints Array of distance breakpoints to check. Should be 1 element smaller than decimals.
   */
  public parseToFriendlyDistanceCustomDecimal(
    distance: number,
    decimals: number[],
    distanceBreakpoints: number[],
    useCommas: boolean = false
  ): string {
    if (decimals.length - distanceBreakpoints.length !== 1) {
      console.warn('unexpected paramater values in parseToFriendlyDistanceCustomDecimal falling back to parseToFriendlyDistance');
      return this.parseToFriendlyDistance(distance, useCommas);
    }

    if (this.unitSystem() === UnitSystem.Imperial) {
      // Convert to decimal miles
      const mi = distance * 0.00062137;

      for (let i = 0; i < distanceBreakpoints.length; i++) {
        if (mi < distanceBreakpoints[i]) {
          return (useCommas ? this.numberWithCommas(mi.toFixed(decimals[i])) : mi.toFixed(decimals[i])) + ' mi';
        }
      }

      return (
        (useCommas ? this.numberWithCommas(mi.toFixed(decimals[decimals.length - 1])) : mi.toFixed(decimals[decimals.length - 1])) + ' mi'
      );
    }
    const km = distance / 1000;
    for (let i = 0; i < distanceBreakpoints.length; i++) {
      if (km < distanceBreakpoints[i]) {
        return (useCommas ? this.numberWithCommas(km.toFixed(decimals[i])) : km.toFixed(decimals[i])) + ' km';
      }
    }

    return (
      (useCommas ? this.numberWithCommas(km.toFixed(decimals[decimals.length - 1])) : km.toFixed(decimals[decimals.length - 1])) + ' km'
    );
  }

  /**
   * Returns heading from point1 to point2 in degrees clockwise from north in the range [-180, 180)
   * @param point1 heading from
   * @param point2 heading to
   * @param headingDeviation
   */
  public getPolygonCoordinatesFromPointsAndArcHeadingDeviation(
    point1: DistanceCoordinate,
    point2: DistanceCoordinate,
    headingDeviation: number
  ): Coordinates {
    const p1 = new google.maps.LatLng(point1.lat, point1.lng);
    const p2 = new google.maps.LatLng(point2.lat, point2.lng);
    const heading = google.maps.geometry.spherical.computeHeading(p1, p2);
    const headingmin = -180 > heading - headingDeviation ? heading - headingDeviation + 360 : heading - headingDeviation;

    const distance = google.maps.geometry.spherical.computeDistanceBetween(p1, p2);

    const coords = [];
    coords.push([p1.lng(), p1.lat()]);
    for (let i = 0; i < headingDeviation * 2; i++) {
      const pp = google.maps.geometry.spherical.computeOffset(p1, distance, headingmin + i > 180 ? headingmin + i : headingmin + i - 360);
      coords.push([pp.lng(), pp.lat()]);
    }
    coords.push([p1.lng(), p1.lat()]);

    return coords;
  }

  /**
   * Returns heading from point1 to point2 in degrees clockwise from north in the range [-180, 180)
   * @param point1 heading from
   * @param point2 heading to
   */
  public getHeadingInDegrees(point1: DistanceCoordinate, point2: DistanceCoordinate): number {
    const heading = google.maps.geometry.spherical.computeHeading(
      new google.maps.LatLng(point1.lat, point1.lng),
      new google.maps.LatLng(point2.lat, point2.lng)
    );
    // Adding negative == subtraction
    return heading < 0 ? 360 + heading : heading;
  }

  /**
   * Returns CardinalDirection north, south, east, or west. Returns null if there was an issue determining the direction
   * @param point1 direction from
   * @param point2 direction to
   */
  public getCardinalDirectionFromMapCenter(
    mapCenter: DistanceCoordinate,
    point2: DistanceCoordinate,
    mapBounds: { nwCoordinate: DistanceCoordinate; seCoordinate: DistanceCoordinate }
  ): CardinalDirection {
    const nwLat = mapBounds.nwCoordinate.lat;
    const nwLng = mapBounds.nwCoordinate.lng;

    const neLat = mapBounds.nwCoordinate.lat;
    const neLng = mapBounds.seCoordinate.lng;

    const seLat = mapBounds.seCoordinate.lat;
    const seLng = mapBounds.seCoordinate.lng;

    const swLat = mapBounds.seCoordinate.lat;
    const swLng = mapBounds.nwCoordinate.lng;

    const neHeading = this.getHeadingInDegrees(mapCenter, new DistanceCoordinate(neLat, neLng));
    const seHeading = this.getHeadingInDegrees(mapCenter, new DistanceCoordinate(seLat, seLng));
    const swHeading = this.getHeadingInDegrees(mapCenter, new DistanceCoordinate(swLat, swLng));
    const nwHeading = this.getHeadingInDegrees(mapCenter, new DistanceCoordinate(nwLat, nwLng));

    const heading = this.getHeadingInDegrees(mapCenter, point2);

    if (heading > 360) {
      return null;
    }

    if (neHeading <= heading && heading <= seHeading) {
      return CardinalDirection.east;
    }
    if (seHeading <= heading && heading <= swHeading) {
      return CardinalDirection.south;
    }
    if (swHeading <= heading && heading <= nwHeading) {
      return CardinalDirection.west;
    }
    if (heading <= neHeading || nwHeading <= heading) {
      return CardinalDirection.north;
    }

    // Failsafe return null, probably not needed
    return null;
  }

  public latLngInDatasetBounds(point: DistanceCoordinate, bounds: BoundingBox): boolean {
    const neLat = bounds.nwCoordinate.lat;
    const neLng = bounds.seCoordinate.lng;

    const swLat = bounds.seCoordinate.lat;
    const swLng = bounds.nwCoordinate.lng;

    const latLngBounds = new google.maps.LatLngBounds(new google.maps.LatLng(swLat, swLng), new google.maps.LatLng(neLat, neLng));

    return latLngBounds.contains(new google.maps.LatLng(point.lat, point.lng));
  }

  public latLngInBounds(point: DistanceCoordinate, latLngBounds: google.maps.LatLngBounds): boolean {
    return latLngBounds.contains(new google.maps.LatLng(point.lat, point.lng));
  }

  private getCenterFromPolygon(coordinates: Coordinates): DistanceCoordinate {
    const poly = new google.maps.Polygon({ paths: this.flatten(coordinates) });
    const bounds = new google.maps.LatLngBounds();
    const paths = poly.getPath();
    paths?.forEach(element => bounds.extend(element));
    const center = bounds.getCenter();

    return new DistanceCoordinate(center.lat(), center.lng());
  }

  private getCenterFromPolyline(coordinates: Coordinates): DistanceCoordinate {
    const poly = new google.maps.Polyline({ path: this.flatten(coordinates) as google.maps.LatLng[] });
    const bounds = new google.maps.LatLngBounds();
    poly.getPath().forEach(element => bounds.extend(element));
    const center = bounds.getCenter();

    return new DistanceCoordinate(center.lat(), center.lng());
  }

  private extractLatLng(array: [number, number]): google.maps.LatLng {
    return new google.maps.LatLng(array[1], array[0]);
  }

  private flatten(list: Coordinates): google.maps.LatLng[][] | google.maps.LatLng[] {
    if (Array.isArray(list) && list.length && typeof list[0] === 'number') {
      return [this.extractLatLng(list as [number, number])];
    }
    return (list as [number, number][]).reduce((acc, item) => {
      if (Array.isArray(item) && Array.isArray(item[0])) {
        return acc.concat(this.flatten(item));
      } else {
        acc.push(this.extractLatLng(item as [number, number]));
        return acc;
      }
    }, []);
  }

  private normalizeToEdges(list: Coordinates): [number, number][][][] {
    if (!Array.isArray(list) || !list.length) {
      return [[[]]];
    }
    if (typeof list[0] === 'number') {
      return [[[list as [number, number]]]];
    }
    if (!Array.isArray(list[0]) || !list[0].length) {
      return [[[]]];
    }
    if (typeof list[0][0] === 'number') {
      return [[(list as [number, number][]).map(coord => coord as [number, number])]];
    }
    if (!Array.isArray(list[0][0]) || !list[0][0].length) {
      return [[[]]];
    }
    if (typeof list[0][0][0] === 'number') {
      return [(list as [number, number][][]).map(line => line.map(coord => coord as [number, number]))];
    }
    if (!Array.isArray(list[0][0]) || !list[0][0].length) {
      return [[[]]];
    }
    if (typeof list[0][0][0] === 'number') {
      return (list as [number, number][][][]).map(polygon => polygon.map(line => line.map(coord => coord as [number, number])));
    }
    console.error('encountered unexpected coordinate array 4 levels deep!');
    return [[[]]];
  }
}
